import { inject, InjectionToken, PLATFORM_ID, RESPONSE_INIT } from '@angular/core';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ResolveFn, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap, map } from 'rxjs/operators';

import { StrapiService } from '../../core/services/strapi.service';
import { CmsResolveResult, RedirectResolveResult, ResolveResult } from '../../core/models/resolve.models';
import { StaticPage, StaticPageParent } from '../../core/models/static-page.models';

/**
 * quick-65 / spec 30 — Derive the canonical URL for a static-page entry by
 * walking its server-populated `parent` chain.
 *
 * Backend `/api/static-pages/by-slug/:slug` returns `parent` as a nested
 * relation ({ slug, parent: { slug, parent: ... } | null }). The canonical
 * URL is the oldest-first concatenation of ancestor slugs + the entry's own
 * slug, joined by '/'.
 *
 * Examples:
 *   entry = { slug: 'asset-audit', parent: { slug: 'contact', parent: null } }
 *     → '/contact/asset-audit'
 *   entry = { slug: 'sam4', parent: null }
 *     → '/sam4'
 *
 * This function is the ONLY canonical source for the static-page catchall
 * bug. `/api/resolve` echoes the requested path back verbatim and cannot be
 * used (see STATE.md quick-65 blocker entry — resolved via Option B).
 */
function deriveStaticPageCanonical(entry: StaticPage): string {
  const slugs: string[] = [];
  let node: StaticPageParent | null | undefined = entry.parent;
  while (node) {
    if (node.slug) slugs.unshift(node.slug);
    node = node.parent;
  }
  slugs.push(entry.slug);
  return '/' + slugs.join('/');
}

/**
 * Attaches the fully fetched static-page entry + archive-pages list to the
 * resolveResult so StaticPageComponent can render synchronously, with no
 * in-component HTTP.
 *
 * Why: under SSR, an HTTP fired from a component constructor effect is
 * fire-and-forget — the platform injector tears down once the synchronous
 * render completes, and the late HTTP response lands in a destroyed injector,
 * producing NG0205. Doing the fetch in the resolver makes SSR await it.
 *
 * Archive-pages are forkJoined alongside the entry fetch so `_relationBasePath`
 * on related-section entries (e.g. homepage asset-type cards) can be populated
 * by `resolveRelationFields` in the component. Without the archive list, cards
 * fall back to `/{slug}` instead of `/{archive-base-path}/{slug}`.
 *
 * quick-65: the canonical-URL guard folds into this forkJoin. Static-pages are
 * the only CPT hit by the spec-30 duplicate-URL bug (every other CPT is mounted
 * under a fixed archive prefix whose resolver-assigned path matches the request
 * by construction). We already pay a by-slug round trip here, so deriving the
 * canonical from its `parent` chain is zero extra network on the hot path. Only
 * non-canonical requests pay the extra cost — which is exactly what we're
 * trying to collapse. On mismatch the map stage returns `null` (instead of the
 * attached CmsResolveResult) and dispatches a 301 via redirectToCanonical.
 */
function attachStaticPageEntry(
  result: CmsResolveResult,
  strapi: StrapiService,
  requestedPath: string,
  router: Router,
  isServer: boolean,
  responseInit: ResponseInit | null,
) {
  return forkJoin({
    entryRes: strapi.findStaticPageBySlug(result.slug),
    allArchives: strapi.findArchivePages().pipe(
      catchError(() => of({ data: [], meta: {} })),
    ),
  }).pipe(
    map(({ entryRes, allArchives }) => {
      const entry = entryRes.data as StaticPage | null;

      // quick-65 / spec 30 canonical-URL guard.
      // Skip when:
      //  - requestedPath is '/': the synthetic root short-circuit would
      //    otherwise loop `/` → `/home` because the home entry's canonical
      //    (derived from its own slug + parent chain) is `/home`. Root is
      //    already canonical by convention.
      //  - entry is null: downstream 404 logic handles this; guard is moot.
      if (entry && requestedPath !== '/') {
        const canonical = deriveStaticPageCanonical(entry);
        if (canonical && canonical !== requestedPath) {
          redirectToCanonical(canonical, router, isServer, responseInit);
          return null;
        }
      }

      return {
        ...result,
        prefetchedEntry: entry,
        prefetchedArchives: allArchives.data,
      } as CmsResolveResult;
    }),
  );
}

/**
 * Same SSR-safe prefetch pattern as static-page (LAND-03/04) + archive-pages
 * prefetch so related-section cards resolve to their archive base path.
 * Landing pages use the stock collection endpoint with the 'full' populate preset.
 */
function attachLandingPageEntry(
  result: CmsResolveResult,
  strapi: StrapiService,
  router: Router,
  isServer: boolean,
  responseInit: ResponseInit | null,
) {
  return forkJoin({
    entryRes: strapi.findLandingPageBySlug(result.slug),
    allArchives: strapi.findArchivePages().pipe(
      catchError(() => of({ data: [], meta: {} })),
    ),
  }).pipe(
    map(({ entryRes, allArchives }) => {
      // 41-03 / SEO-03: helper-internal soft-404 → real-404 guard.
      // Mirrors the static-page guard pattern; pushes notFound() inside the
      // helper so call sites don't all need duplicate boilerplate. Idempotent
      // with the /lp/<slug> defensive outer guard at the call site below.
      if (!entryRes.data) {
        notFound(router, isServer, responseInit);
        return null;
      }
      return {
        ...result,
        prefetchedEntry: entryRes.data,
        prefetchedArchives: allArchives.data,
      } as CmsResolveResult;
    }),
  );
}

/**
 * Same SSR-safe prefetch pattern as static-page + archive-pages prefetch so
 * related-section cards resolve to their archive base path. Partners use the
 * custom `/partners/by-slug/:slug` endpoint via findByCustomSlug.
 */
function attachPartnerEntry(
  result: CmsResolveResult,
  strapi: StrapiService,
  router: Router,
  isServer: boolean,
  responseInit: ResponseInit | null,
) {
  return forkJoin({
    entryRes: strapi.findPartnerBySlug(result.slug),
    allArchives: strapi.findArchivePages().pipe(
      catchError(() => of({ data: [], meta: {} })),
    ),
  }).pipe(
    map(({ entryRes, allArchives }) => {
      // 41-03 / SEO-03: missing partner slug → real HTTP 404 (was soft-404).
      if (!entryRes.data) {
        notFound(router, isServer, responseInit);
        return null;
      }
      return {
        ...result,
        prefetchedEntry: entryRes.data,
        prefetchedArchives: allArchives.data,
      } as CmsResolveResult;
    }),
  );
}

/**
 * Same SSR-safe prefetch pattern as static-page / partner + archive-pages
 * prefetch. Event-pages use the custom `/event-pages/by-slug/:slug` endpoint
 * via findByCustomSlug (DZ-only CPT — filter-based populate=* hits 431 URI
 * errors on deep block trees; STATE decision 22-03 / 31-03).
 */
function attachEventPageEntry(
  result: CmsResolveResult,
  strapi: StrapiService,
  router: Router,
  isServer: boolean,
  responseInit: ResponseInit | null,
) {
  return forkJoin({
    entryRes: strapi.findEventBySlug(result.slug),
    allArchives: strapi.findArchivePages().pipe(
      catchError(() => of({ data: [], meta: {} })),
    ),
  }).pipe(
    map(({ entryRes, allArchives }) => {
      // 41-03 / SEO-03: missing event slug → real HTTP 404. Replaces the
      // legacy event-page.component.ts:102 client-side router.navigate(['/404'])
      // soft-404 — that line is deleted in this same task.
      if (!entryRes.data) {
        notFound(router, isServer, responseInit);
        return null;
      }
      return {
        ...result,
        prefetchedEntry: entryRes.data,
        prefetchedArchives: allArchives.data,
      } as CmsResolveResult;
    }),
  );
}

/**
 * Spec 36 (Phase 45). Same SSR-safe prefetch pattern as event-page above —
 * by-slug via getWebinarPageBySlug (which routes through findByCustomSlug for
 * the custom `/webinar-pages/by-slug/<slug>` controller; backend handles deep
 * populate server-side per STATE 22-03/31-03 decision). Webinar-pages live at
 * `/resources/webinars/<slug>` (canonical URL — backend archive entry has
 * `basePath: "/resources"`). prefetchedArchives forkJoined for related-section
 * card basePath resolution.
 *
 * WBNR-12: missing webinar slug → real HTTP 404 (mirrors event-page's
 * notFound guard pattern).
 */
function attachWebinarPageEntry(
  result: CmsResolveResult,
  strapi: StrapiService,
  router: Router,
  isServer: boolean,
  responseInit: ResponseInit | null,
) {
  return forkJoin({
    entryRes: strapi.getWebinarPageBySlug(result.slug),
    allArchives: strapi.findArchivePages().pipe(
      catchError(() => of({ data: [], meta: {} })),
    ),
  }).pipe(
    map(({ entryRes, allArchives }) => {
      // WBNR-12 — true HTTP 404 via notFound guard. Mirrors event-page's
      // helper-internal soft-404 → real-404 guard (line ~206).
      if (!entryRes.data) {
        notFound(router, isServer, responseInit);
        return null;
      }
      return {
        ...result,
        prefetchedEntry: entryRes.data,
        prefetchedArchives: allArchives.data,
      } as CmsResolveResult;
    }),
  );
}

/**
 * Spec 34 (quick-70). Same SSR-safe prefetch pattern as landing-page —
 * by-slug via findByCustomSlug, prefetchedArchives forkJoined for
 * related-section card basePath resolution. Lead-magnet-page is a DZ-only
 * CPT with no `parent` chain and no canonical guard, so this is the
 * smallest of the DZ-only prefetch helpers.
 */
function attachLeadMagnetPageEntry(
  result: CmsResolveResult,
  strapi: StrapiService,
  router: Router,
  isServer: boolean,
  responseInit: ResponseInit | null,
) {
  return forkJoin({
    entryRes: strapi.findLeadMagnetPageBySlug(result.slug),
    allArchives: strapi.findArchivePages().pipe(
      catchError(() => of({ data: [], meta: {} })),
    ),
  }).pipe(
    map(({ entryRes, allArchives }) => {
      // 41-03 / SEO-03: missing lead-magnet slug → real HTTP 404.
      if (!entryRes.data) {
        notFound(router, isServer, responseInit);
        return null;
      }
      return {
        ...result,
        prefetchedEntry: entryRes.data,
        prefetchedArchives: allArchives.data,
      } as CmsResolveResult;
    }),
  );
}

/**
 * Honour a `{ type: 'redirect' }` response from `/api/resolve` (quick-52 Task 4).
 *
 * Two platforms, one shared mechanism — `Router.navigateByUrl`:
 *   - On SSR we additionally mutate `RESPONSE_INIT.status` (the Angular 21
 *     SSR response-init object shared by reference with `@angular/ssr`'s
 *     render pipeline in `node_modules/@angular/ssr/fesm2022/ssr.mjs:1256-1296`).
 *     When the router navigates to a different URL during SSR, the engine
 *     detects the navigation diff and calls `createRedirectResponse(redirectTo,
 *     responseInit.status, headers)` — so whatever we set here becomes the
 *     real HTTP status code (default is 302 if we don't touch it). This is
 *     the repo-native pattern for writing SSR response metadata from inside
 *     Angular, analogous to how PREVIEW_VERIFIER (decision 37-02) injects
 *     server-only tokens.
 *   - On the browser we do the SPA nav WITHOUT `replaceUrl` (quick-112
 *     follow-up to quick-109) so a new history entry is pushed for the
 *     redirect destination. The browser back-button then restores the page
 *     the user actually came from (not the redirect source URL, which is
 *     cancelled before Angular pushes it). The previous unconditional
 *     `replaceUrl: true` collapsed the user's prior page out of history.
 *     No `location.replace()` — that's the partially-wired pattern this
 *     function replaces.
 *
 * Either way the resolver returns `of(null)`. On SSR, Angular's renderer
 * short-circuits to the redirect Response before any HTML body is produced;
 * on the browser, CmsPageComponent sees `resolveResult()` as null during the
 * brief moment before the SPA nav kicks in.
 *
 * Redirect-middleware note: `src/redirect-middleware.ts` handles a DIFFERENT
 * redirect surface (the Strapi `/api/redirects` collection, matched by an
 * Express middleware BEFORE Angular runs). This function handles dynamic
 * redirects from `/api/resolve` for paths that fall THROUGH that middleware.
 */
function handleRedirect(
  result: RedirectResolveResult,
  router: Router,
  isServer: boolean,
  responseInit: ResponseInit | null,
): void {
  if (isServer && responseInit) {
    responseInit.status = result.statusCode;
  }
  // navigateByUrl returns a Promise<boolean>; we deliberately don't await it
  // — Angular's SSR engine tracks the pending navigation via the router's
  // internal state and emits the redirect Response once routing settles.
  // In the browser the nav happens async anyway.
  //
  // QUICK-112 follow-up to quick-109: `replaceUrl: isServer` (NOT
  // `replaceUrl: true` unconditionally). In SSR the response IS the redirect
  // — replaceUrl is a no-op for browser history because the browser receives
  // a 301/302 and handles its own history mutation. In SPA mode
  // `replaceUrl: true` replaces the PREVIOUS history entry (because the
  // in-flight nav to the redirect source is cancelled before Angular pushes
  // it), so back-button skips past the user's actual prior page. Pushing a
  // new entry lets the browser back button restore the page the user came
  // from.
  void router.navigateByUrl(result.destination, { replaceUrl: isServer });
}

/**
 * quick-65 / spec 30 — 301 a request to its canonical URL.
 *
 * Mirrors `handleRedirect`'s SSR-write + SPA-nav mechanism but is always a
 * 301 (vs. `handleRedirect`'s dynamic status from the backend RedirectResolveResult).
 * Kept separate so types stay honest: this function takes a plain path; it
 * does not participate in the /api/resolve redirect contract.
 *
 * Why a separate helper rather than synthesising a `RedirectResolveResult`:
 * canonical-URL collapses are a frontend-only concern (the backend echoes
 * request paths and can't produce them), while RedirectResolveResult models
 * a backend decision. Mixing them would muddy the resolver's
 * narrow-on-result.type switch.
 */
function redirectToCanonical(
  canonicalPath: string,
  router: Router,
  isServer: boolean,
  responseInit: ResponseInit | null,
): void {
  if (isServer && responseInit) {
    responseInit.status = 301;
  }
  void router.navigateByUrl(canonicalPath, { replaceUrl: true });
}

/**
 * quick-60: Write a real HTTP 404 for unresolvable paths while keeping the
 * existing UX (NotFoundPageComponent rendered via skipLocationChange).
 *
 * Mirrors handleRedirect's RESPONSE_INIT mutation pattern: on SSR we mutate
 * the shared ResponseInit object so @angular/ssr's render pipeline emits the
 * status we want. On the browser responseInit is null and we just do the
 * SPA nav so the user sees the 404 body — the "status" concept doesn't exist
 * client-side.
 */
function notFound(
  router: Router,
  isServer: boolean,
  responseInit: ResponseInit | null,
): void {
  if (isServer && responseInit) {
    responseInit.status = 404;
  }
  router.navigate(['/404'], { skipLocationChange: true });
}

/** Full-page recovery uses the server-rendered route, which already handles
 * upstream failures as a non-cacheable 503. The returned error page contains no
 * Angular app, so a persistent outage cannot create an automatic reload loop.
 * Injectable to test recovery without navigating the test runner.
 */
export const CMS_DOCUMENT_NAVIGATION = new InjectionToken<(url: string) => void>(
  'CMS document navigation',
  {
    providedIn: 'root',
    factory: () => {
      const document = inject(DOCUMENT);
      return (url: string) => document.location.assign(url);
    },
  },
);

export const cmsPageResolver: ResolveFn<CmsResolveResult | null> = (route, state) => {
  const strapi = inject(StrapiService);
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);
  const responseInit = inject(RESPONSE_INIT, { optional: true });
  const isServer = isPlatformServer(platformId);
  const segments = route.url.map((s) => s.path);
  const documentNavigate = inject(CMS_DOCUMENT_NAVIGATION);
  // RouterStateSnapshot retains the requested query and fragment while the
  // address bar still shows the page the visitor is leaving.
  const requestedUrl = state.url || '/' + segments.join('/');
  const recoverRequestFailure = (error: unknown) => {
    if (isServer || (error instanceof HttpErrorResponse && error.status === 404)) {
      // SSR's response boundary distinguishes upstream errors from true 404s.
      notFound(router, isServer, responseInit);
    } else {
      // Do not convert timeout/network/5xx failures into a false missing page.
      // A native GET also avoids repeating the same browser-to-CMS request.
      documentNavigate(requestedUrl);
    }
    return of(null);
  };


  // STAT-05: backend /api/resolve does NOT special-case '/' (spec lines 34-35).
  // Frontend short-circuits the home page to a synthetic resolve result that
  // points CmsPageComponent at the static-page with slug 'home', bypassing
  // /api/resolve entirely so we never get a spurious 404 on root.
  if (segments.length === 0) {
    const synthetic: CmsResolveResult = {
      type: 'static-page',
      contentType: 'static-page',
      slug: 'home',
      path: '/',
      documentId: '',
    };
    // Pass requestedPath='/' so the canonical-URL guard inside
    // attachStaticPageEntry exempts root (otherwise the home entry's canonical
    // would be derived as `/home` from its slug and we'd 301-loop). Other args
    // threaded through so the same helper can be reused on the main CMS path
    // below without duplicating the redirect machinery.
    return attachStaticPageEntry(synthetic, strapi, '/', router, isServer, responseInit).pipe(
      map((res) => {
        // quick-78: guard against null/empty home entry. Without this,
        // findStaticPageBySlug('home') resolving with `data: null` would
        // produce a CmsResolveResult with `prefetchedEntry: null`, leaving
        // CmsPageComponent rendering nothing — Screaming Frog crawls saw the
        // default Angular shell (<title>SamoticsWebsite</title>, 0 <a href>)
        // and stopped at depth 1. Mirror /lp/<slug> guard (resolver.ts:346-352).
        if (!res || !res.prefetchedEntry) {
          notFound(router, isServer, responseInit);
          return null;
        }
        return res;
      }),
      catchError(recoverRequestFailure),
    );
  }

  const path = '/' + segments.join('/');

  // LAND-07: landing-pages live under `/lp/<slug>`. Bare `/lp` (no slug) must
  // 404 — marketing campaign listings are never public. For `/lp/<slug>` we
  // short-circuit /api/resolve (which does not register the /lp prefix) and
  // fetch the landing-page directly by slug, mirroring the 22-03 root `/`
  // short-circuit pattern for static-pages.
  if (segments[0] === 'lp') {
    if (segments.length < 2) {
      notFound(router, isServer, responseInit);
      return of(null);
    }
    const lpSlug = segments.slice(1).join('/');
    const synthetic: CmsResolveResult = {
      type: 'landing-page',
      contentType: 'landing-page',
      slug: lpSlug,
      path,
      documentId: '',
    };
    return attachLandingPageEntry(synthetic, strapi, router, isServer, responseInit).pipe(
      map((res) => {
        // 41-03: defensive duplicate of the helper-internal guard. Idempotent —
        // the second `responseInit.status = 404` write is a no-op. Kept for
        // robustness if the helper signature ever drifts back to soft-404.
        if (!res || !res.prefetchedEntry) {
          notFound(router, isServer, responseInit);
          return null;
        }
        return res;
      }),
      catchError(recoverRequestFailure),
    );
  }

  return strapi.resolve(path).pipe(
    switchMap((result: ResolveResult) => {
      // quick-52 Task 4: honour redirect responses from /api/resolve.
      // SSR emits a real HTTP 301/302/307/308 via RESPONSE_INIT + router nav;
      // browser does SPA nav with replaceUrl.
      if (result.type === 'redirect') {
        handleRedirect(result, router, isServer, responseInit);
        return of(null);
      }

      // LAND-07: never render a landing-page archive listing publicly.
      // If the backend returns the /lp archive-page stub (or any archive-page
      // whose contentType is 'landing-page'), short-circuit to /404 so the
      // marketing campaign collection is never exposed as a public index.
      if (
        result.type === 'archive-page' &&
        result.contentType === 'landing-page'
      ) {
        notFound(router, isServer, responseInit);
        return of(null);
      }
      if (result.type === 'static-page') {
        // quick-65: pass the requested `path` so attachStaticPageEntry can
        // 301-redirect to the canonical URL derived from the entry's parent
        // chain when the requested prefix is non-canonical (spec 30).
        return attachStaticPageEntry(result, strapi, path, router, isServer, responseInit);
      }
      if (result.type === 'landing-page') {
        return attachLandingPageEntry(result, strapi, router, isServer, responseInit);
      }
      if (result.type === 'partner') {
        return attachPartnerEntry(result, strapi, router, isServer, responseInit);
      }
      if (result.type === 'event-page') {
        return attachEventPageEntry(result, strapi, router, isServer, responseInit);
      }
      if (result.type === 'webinar-page') {
        return attachWebinarPageEntry(result, strapi, router, isServer, responseInit);
      }
      if (result.type === 'lead-magnet-page') {
        return attachLeadMagnetPageEntry(result, strapi, router, isServer, responseInit);
      }
      return of(result);
    }),
    catchError(recoverRequestFailure),
  );
};
